import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { PercentIcon, CalendarIcon, UserIcon, SaveIcon, StethoscopeIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Tabs } from '../ui/Tabs';
import { Pagination } from '../ui/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../utils/supabase';
import { formatCurrency, getToday, getDoctorPrefix } from '../../utils/helpers';
interface CommissionConfig {
  defaultPercent: number;
  userPercents: Record<string, number>;
}
const STORAGE_KEY = 'savidna-commission-config';
function loadConfigFromLocalStorage(): CommissionConfig {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate from old format
      if (typeof parsed.globalPercent === 'number' && !parsed.userPercents) {
        return {
          defaultPercent: parsed.globalPercent,
          userPercents: {}
        };
      }
      return {
        defaultPercent: typeof parsed.defaultPercent === 'number' ? parsed.defaultPercent : 10,
        userPercents: parsed.userPercents || {}
      };
    }
  } catch (e) {
    console.error('Error loading commission config', e);
  }
  return {
    defaultPercent: 10,
    userPercents: {}
  };
}
interface CommissionPerson {
  id: string;
  name: string;
  type: 'usuario' | 'doctor';
}
function getActiveUserStatuses(): Record<string, string> {
  try {
    const saved = localStorage.getItem('savidna-user-statuses');
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}
function getDoctorStatuses(): Record<string, string> {
  try {
    const saved = localStorage.getItem('savidna-doctors-status');
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
}
export function Commissions() {
  const {
    appointments,
    doctors,
    getPatient,
    getDoctor,
    getService,
    getSpecialty
  } = useData();
  const {
    isConnectedToSupabase
  } = useAuth();
  // Commission config state
  const [config, setConfig] = useState<CommissionConfig>(loadConfigFromLocalStorage);
  const [editingPercents, setEditingPercents] = useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const isSavingToSupabase = useRef(false);
  const userHasEdited = useRef(false); // Only true when user manually changes a percent
  // Filter State
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState<string>(getToday());
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  // All people (users + doctors), excluding deactivated/archived
  const allPeople = useMemo(() => {
    const userStatuses = getActiveUserStatuses();
    const doctorStatuses = getDoctorStatuses();
    const people: CommissionPerson[] = [];
    const seenIds = new Set<string>();
    // Users from appointments
    appointments.forEach(apt => {
      if (apt.createdByUserId && apt.createdByUserName && !seenIds.has(apt.createdByUserId)) {
        const status = userStatuses[apt.createdByUserId];
        if (status === 'inactive' || status === 'archived') return;
        seenIds.add(apt.createdByUserId);
        people.push({
          id: apt.createdByUserId,
          name: apt.createdByUserName,
          type: 'usuario'
        });
      }
    });
    // Doctors
    doctors.forEach(doc => {
      const docKey = `doctor-${doc.id}`;
      const status = doctorStatuses[doc.id];
      if (status === 'archived' || status === 'deactivated') return;
      if (!seenIds.has(docKey)) {
        seenIds.add(docKey);
        const prefix = getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name);
        people.push({
          id: docKey,
          name: `${prefix} ${doc.name}`,
          type: 'doctor'
        });
      }
    });
    return people.sort((a, b) => a.name.localeCompare(b.name));
  }, [appointments, doctors, getSpecialty]);
  // Load config from Supabase on mount
  useEffect(() => {
    async function loadFromSupabase() {
      if (!isConnectedToSupabase) return;
      setIsLoadingConfig(true);
      try {
        const {
          data,
          error
        } = await supabase.from('commission_configs').select('person_id, commission_percent').eq('is_active', true);
        if (!error && data && data.length > 0) {
          const userPercents: Record<string, number> = {};
          data.forEach((row: any) => {
            userPercents[row.person_id] = Number(row.commission_percent);
          });
          const newConfig: CommissionConfig = {
            defaultPercent: config.defaultPercent,
            userPercents: {
              ...config.userPercents,
              ...userPercents
            }
          };
          setConfig(newConfig);
          // Sync to localStorage as cache
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
        }
      } catch (e) {
        console.error('Error loading commission configs from Supabase:', e);
      } finally {
        setIsLoadingConfig(false);
      }
    }
    loadFromSupabase();
  }, [isConnectedToSupabase]);
  // Initialize editing percents from config
  // Only re-run when allPeople changes (NOT when config changes from our own save)
  const configLoadedRef = useRef(false);
  const prevAllPeopleIdsRef = useRef<string>('');
  useEffect(() => {
    const currentIds = allPeople.map(p => p.id).join(',');
    const peopleListChanged = currentIds !== prevAllPeopleIdsRef.current;
    prevAllPeopleIdsRef.current = currentIds;
    const initial: Record<string, string> = {};
    allPeople.forEach(p => {
      if (configLoadedRef.current && editingPercents[p.id] !== undefined) {
        initial[p.id] = editingPercents[p.id];
      } else {
        const val = configRef.current.userPercents[p.id] ?? configRef.current.defaultPercent;
        initial[p.id] = String(val);
      }
    });
    setEditingPercents(initial);
    configLoadedRef.current = true;
  }, [allPeople]);
  // Pagination for config table
  const configPagination = usePagination(allPeople, {
    itemsPerPage: 6
  });
  const handlePercentChange = (personId: string, value: string) => {
    userHasEdited.current = true;
    setEditingPercents(prev => ({
      ...prev,
      [personId]: value
    }));
  };
  // Real-time: derive commission percent from editingPercents (live input values)
  const getCommissionPercent = useCallback((personId: string | undefined): number => {
    if (!personId) return config.defaultPercent;
    const editVal = editingPercents[personId];
    if (editVal !== undefined && editVal !== '') {
      const parsed = parseFloat(editVal);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) return parsed;
    }
    return config.userPercents[personId] ?? config.defaultPercent;
  }, [config, editingPercents]);
  // Build userPercents from editingPercents, preserving existing saved values
  function buildUserPercents(): Record<string, number> {
    // Start with ALL previously saved percents (don't drop people not in current allPeople)
    const result: Record<string, number> = {
      ...config.userPercents
    };
    // Override with current editing values for visible people
    allPeople.forEach(p => {
      const val = parseFloat(editingPercents[p.id] ?? String(config.defaultPercent));
      if (!isNaN(val) && val >= 0 && val <= 100) {
        result[p.id] = val;
      }
    });
    return result;
  }
  // Save to localStorage — update config ref without triggering re-initialization
  const configRef = useRef(config);
  configRef.current = config;
  function saveToLocalStorage(userPercents: Record<string, number>) {
    const newConfig: CommissionConfig = {
      defaultPercent: configRef.current.defaultPercent,
      userPercents
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
      // Update config state but prevent re-initialization of editingPercents
      configRef.current = newConfig;
      setConfig(newConfig);
      return true;
    } catch (e) {
      console.error('Error saving commission config to localStorage:', e);
      return false;
    }
  }
  // Save to Supabase
  async function saveToSupabase(userPercents: Record<string, number>): Promise<boolean> {
    if (!isConnectedToSupabase) {
      console.log('Commission save: Not connected to Supabase, skipping');
      return false;
    }
    try {
      const upserts = allPeople.map(p => ({
        person_id: p.id,
        person_name: p.name,
        person_type: p.type,
        commission_percent: userPercents[p.id] ?? config.defaultPercent,
        is_active: true
      }));
      if (upserts.length === 0) {
        console.log('Commission save: No people to save');
        return false;
      }
      console.log('Commission save: Attempting to save', upserts.length, 'records to Supabase...');
      const {
        data,
        error
      } = await supabase.from('commission_configs').upsert(upserts, {
        onConflict: 'person_id'
      }).select();
      if (error) {
        console.error('❌ Supabase commission_configs error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        setSaveMessage('❌ Error BD: ' + error.message);
        setTimeout(() => setSaveMessage(''), 5000);
        return false;
      }
      console.log('✅ Commission configs saved to Supabase:', upserts.length, 'records');
      return true;
    } catch (e: any) {
      console.error('❌ Exception saving commission configs:', e?.message || e);
      setSaveMessage('❌ Error: ' + (e?.message || 'Error desconocido'));
      setTimeout(() => setSaveMessage(''), 5000);
      return false;
    }
  }
  // Auto-save: debounced persist ONLY when user has manually edited a percent
  useEffect(() => {
    if (!configLoadedRef.current) return;
    if (!userHasEdited.current) return;
    const timer = setTimeout(() => {
      const userPercents = buildUserPercents();
      saveToLocalStorage(userPercents);
      if (isConnectedToSupabase && !isSavingToSupabase.current) {
        isSavingToSupabase.current = true;
        saveToSupabase(userPercents).finally(() => {
          isSavingToSupabase.current = false;
        });
      }
      userHasEdited.current = false;
    }, 800);
    return () => clearTimeout(timer);
  }, [editingPercents]);
  // Manual save button handler
  const handleSaveConfig = async () => {
    const userPercents = buildUserPercents();
    // Save to localStorage
    saveToLocalStorage(userPercents);
    // Save to Supabase
    const supabaseOk = await saveToSupabase(userPercents);
    if (isConnectedToSupabase && supabaseOk) {
      setSaveMessage('Guardado en base de datos ✓');
    } else if (isConnectedToSupabase) {
      setSaveMessage('Guardado local (error en BD)');
    } else {
      setSaveMessage('Configuración guardada');
    }
    setTimeout(() => setSaveMessage(''), 3000);
  };
  // Filtered appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      if (apt.status !== 'completada') return false;
      if (dateFrom && apt.date < dateFrom) return false;
      if (dateTo && apt.date > dateTo) return false;
      if (selectedUserId !== 'all') {
        if (selectedUserId.startsWith('doc-filter-')) {
          const docId = selectedUserId.replace('doc-filter-', '');
          if (apt.doctorId !== docId) return false;
        } else {
          if (apt.createdByUserId !== selectedUserId) return false;
        }
      }
      return true;
    }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [appointments, dateFrom, dateTo, selectedUserId]);
  // Appointments with per-user commission
  const appointmentsWithCommission = useMemo(() => {
    return filteredAppointments.map(apt => {
      const patient = getPatient(apt.patientId);
      const doctor = getDoctor(apt.doctorId);
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      let totalRevenue = 0;
      const serviceNames: string[] = [];
      serviceIds.forEach(sId => {
        const s = getService(sId);
        if (s) {
          totalRevenue += s.price;
          serviceNames.push(s.name);
        }
      });
      const userPercent = getCommissionPercent(apt.createdByUserId);
      const commission = totalRevenue * (userPercent / 100);
      return {
        ...apt,
        patientName: patient?.name || 'Desconocido',
        doctorName: doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : 'Desconocido',
        serviceNames: serviceNames.join(', '),
        totalRevenue,
        commission,
        userPercent,
        registeredBy: apt.createdByUserName || 'Sistema'
      };
    });
  }, [filteredAppointments, getPatient, getDoctor, getService, getSpecialty, getCommissionPercent]);
  // Pagination for appointments table
  const aptPagination = usePagination(appointmentsWithCommission, {
    itemsPerPage: 10
  });
  const totals = useMemo(() => {
    return appointmentsWithCommission.reduce((acc, apt) => {
      acc.revenue += apt.totalRevenue;
      acc.commission += apt.commission;
      return acc;
    }, {
      revenue: 0,
      commission: 0
    });
  }, [appointmentsWithCommission]);
  const userSummary = useMemo(() => {
    const summary: Record<string, {
      userId: string;
      userName: string;
      count: number;
      revenue: number;
      commission: number;
      percent: number;
    }> = {};
    appointmentsWithCommission.forEach(apt => {
      const userId = apt.createdByUserId || 'system';
      const userName = apt.registeredBy;
      if (!summary[userId]) {
        summary[userId] = {
          userId,
          userName,
          count: 0,
          revenue: 0,
          commission: 0,
          percent: apt.userPercent
        };
      }
      summary[userId].count += 1;
      summary[userId].revenue += apt.totalRevenue;
      summary[userId].commission += apt.commission;
    });
    return Object.values(summary).sort((a, b) => b.commission - a.commission);
  }, [appointmentsWithCommission]);
  // Pagination for user summary table
  const userPagination = usePagination(userSummary, {
    itemsPerPage: 6
  });
  // Doctor summary: group by doctor instead of user
  const doctorSummary = useMemo(() => {
    const summary: Record<string, {
      doctorId: string;
      doctorKey: string;
      doctorName: string;
      count: number;
      revenue: number;
      commission: number;
      percent: number;
    }> = {};
    appointmentsWithCommission.forEach(apt => {
      const doctorId = apt.doctorId || 'unknown';
      const doctorKey = `doctor-${doctorId}`;
      const doctorName = apt.doctorName || 'Sin asignar';
      if (!summary[doctorId]) {
        const doctorPercent = getCommissionPercent(doctorKey);
        summary[doctorId] = {
          doctorId,
          doctorKey,
          doctorName,
          count: 0,
          revenue: 0,
          commission: 0,
          percent: doctorPercent
        };
      }
      summary[doctorId].count += 1;
      summary[doctorId].revenue += apt.totalRevenue;
      summary[doctorId].commission += apt.totalRevenue * (summary[doctorId].percent / 100);
    });
    return Object.values(summary).sort((a, b) => b.commission - a.commission);
  }, [appointmentsWithCommission, getCommissionPercent]);
  // Pagination for doctor summary table
  const doctorPagination = usePagination(doctorSummary, {
    itemsPerPage: 6
  });
  // Doctor totals
  const doctorTotals = useMemo(() => {
    return doctorSummary.reduce((acc, doc) => {
      acc.revenue += doc.revenue;
      acc.commission += doc.commission;
      return acc;
    }, {
      revenue: 0,
      commission: 0
    });
  }, [doctorSummary]);
  // Filter options: users + doctors/therapists
  const userOptions = useMemo(() => {
    const options = [{
      value: 'all',
      label: 'Todos'
    }];
    const userStatuses = getActiveUserStatuses();
    const doctorStatuses = getDoctorStatuses();
    const seen = new Set<string>();
    // Users who registered appointments
    appointments.forEach(apt => {
      if (apt.createdByUserId && apt.createdByUserName && !seen.has(apt.createdByUserId)) {
        const status = userStatuses[apt.createdByUserId];
        if (status === 'inactive' || status === 'archived') return;
        seen.add(apt.createdByUserId);
        options.push({
          value: apt.createdByUserId,
          label: `👤 ${apt.createdByUserName}`
        });
      }
    });
    // Doctors/therapists from appointments
    doctors.forEach(doc => {
      const filterKey = `doc-filter-${doc.id}`;
      const status = doctorStatuses[doc.id];
      if (status === 'archived' || status === 'deactivated') return;
      if (!seen.has(filterKey)) {
        seen.add(filterKey);
        const prefix = getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name);
        options.push({
          value: `doc-filter-${doc.id}`,
          label: `🩺 ${prefix} ${doc.name}`
        });
      }
    });
    return options;
  }, [appointments, doctors, getSpecialty]);
  return <div className="space-y-6" data-id="element-1189">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1190">
        <div data-id="element-1191">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-1192">
            <PercentIcon className="w-6 h-6 text-indigo-500" data-id="element-1193" />
            Comisiones
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-1194">
            Gestión y cálculo de comisiones por citas completadas
          </p>
        </div>
      </div>

      {/* Per-Person Commission Configuration */}
      <Card data-id="element-1195">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-1196">
          <CardTitle className="text-base flex items-center gap-2" data-id="element-1197">
            <UserIcon className="w-5 h-5 text-indigo-500" data-id="element-1198" />
            Configuración de Comisión por Persona
          </CardTitle>
          <div className="flex items-center gap-3" data-id="element-1199">
            {saveMessage && <span className="text-sm text-green-600 dark:text-green-400 font-medium" data-id="element-1200">
                ✅ {saveMessage}
              </span>}
            <Button onClick={handleSaveConfig} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-1202" />} size="sm" data-id="element-1201">
              Guardar Todo
            </Button>
          </div>
        </CardHeader>
        <CardContent data-id="element-1203">
          {allPeople.length > 0 ? <>
              <div className="overflow-x-auto" data-id="element-1204">
                <table className="w-full text-sm text-left" data-id="element-1205">
                  <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-1206">
                    <tr data-id="element-1207">
                      <th className="px-6 py-3 font-semibold" data-id="element-1208">Nombre</th>
                      <th className="px-6 py-3 font-semibold text-center w-32" data-id="element-1209">
                        Tipo
                      </th>
                      <th className="px-6 py-3 font-semibold text-center w-48" data-id="element-1210">
                        Comisión (%)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1211">
                    {configPagination.paginatedItems.map(person => <tr key={person.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1212">
                        <td className="px-6 py-3" data-id="element-1213">
                          <div className="flex items-center gap-3" data-id="element-1214">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${person.type === 'doctor' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'}`} data-id="element-1215">
                              {person.type === 'doctor' ? <StethoscopeIcon className="w-4 h-4" data-id="element-1216" /> : <UserIcon className="w-4 h-4" data-id="element-1217" />}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white" data-id="element-1218">
                              {person.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-3 text-center" data-id="element-1219">
                          <Badge variant={person.type === 'doctor' ? 'info' : 'primary'} data-id="element-1220">
                            {person.type === 'doctor' ? 'Doctor / Terapeuta' : 'Usuario'}
                          </Badge>
                        </td>
                        <td className="px-6 py-3" data-id="element-1221">
                          <div className="flex items-center justify-center gap-2" data-id="element-1222">
                            <input type="number" min="0" max="100" step="0.1" value={editingPercents[person.id] ?? ''} onChange={e => handlePercentChange(person.id, e.target.value)} className="w-24 px-3 py-1.5 text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" data-id="element-1223" />
                            <span className="text-gray-500 text-sm" data-id="element-1224">%</span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={configPagination.currentPage} totalPages={configPagination.totalPages} totalItems={configPagination.totalItems} startIndex={configPagination.startIndex} endIndex={configPagination.endIndex} onPageChange={configPagination.goToPage} onNext={configPagination.nextPage} onPrev={configPagination.prevPage} data-id="element-1225" />
            </> : <div className="text-center py-8 text-gray-400 text-sm" data-id="element-1226">
              No se encontraron usuarios o doctores activos.
            </div>}
        </CardContent>
      </Card>

      {/* Filters Section */}
      <Card data-id="element-1227">
        <CardContent className="py-4" data-id="element-1228">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1229">
            <div data-id="element-1230">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1231">
                Desde
              </label>
              <div className="relative" data-id="element-1232">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1233">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-1234" />
                </div>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500" data-id="element-1235" />
              </div>
            </div>
            <div data-id="element-1236">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-1237">
                Hasta
              </label>
              <div className="relative" data-id="element-1238">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1239">
                  <CalendarIcon className="h-4 w-4 text-gray-400" data-id="element-1240" />
                </div>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500" data-id="element-1241" />
              </div>
            </div>
            <div data-id="element-1242">
              <Select label="Usuario / Doctor" options={userOptions} value={selectedUserId} onChange={setSelectedUserId} data-id="element-1243" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1244">
        <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 border-none text-white" data-id="element-1245">
          <CardContent className="p-6" data-id="element-1246">
            <div className="flex items-center justify-between" data-id="element-1247">
              <div data-id="element-1248">
                <p className="text-indigo-100 text-sm font-medium mb-1" data-id="element-1249">
                  Total Ingresos
                </p>
                <h3 className="text-2xl font-bold" data-id="element-1250">
                  {formatCurrency(totals.revenue)}
                </h3>
              </div>
              <div className="p-3 bg-white/20 rounded-xl" data-id="element-1251">
                <PercentIcon className="w-6 h-6 text-white" data-id="element-1252" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 border-none text-white" data-id="element-1253">
          <CardContent className="p-6" data-id="element-1254">
            <div className="flex items-center justify-between" data-id="element-1255">
              <div data-id="element-1256">
                <p className="text-emerald-100 text-sm font-medium mb-1" data-id="element-1257">
                  Total Comisiones
                </p>
                <h3 className="text-2xl font-bold" data-id="element-1258">
                  {formatCurrency(totals.commission)}
                </h3>
              </div>
              <div className="p-3 bg-white/20 rounded-xl" data-id="element-1259">
                <PercentIcon className="w-6 h-6 text-white" data-id="element-1260" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-sky-500 to-blue-600 border-none text-white" data-id="element-1261">
          <CardContent className="p-6" data-id="element-1262">
            <div className="flex items-center justify-between" data-id="element-1263">
              <div data-id="element-1264">
                <p className="text-sky-100 text-sm font-medium mb-1" data-id="element-1265">
                  Citas Completadas
                </p>
                <h3 className="text-2xl font-bold" data-id="element-1266">
                  {appointmentsWithCommission.length}
                </h3>
              </div>
              <div className="p-3 bg-white/20 rounded-xl" data-id="element-1267">
                <CalendarIcon className="w-6 h-6 text-white" data-id="element-1268" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs tabs={[{
      id: 'by-appointment',
      label: 'Comisión por Cita',
      content: <Card data-id="element-1270">
                <div className="overflow-x-auto" data-id="element-1271">
                  <table className="w-full text-sm text-left" data-id="element-1272">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-1273">
                      <tr data-id="element-1274">
                        <th className="px-6 py-4 font-semibold" data-id="element-1275">Fecha</th>
                        <th className="px-6 py-4 font-semibold" data-id="element-1276">Paciente</th>
                        <th className="px-6 py-4 font-semibold" data-id="element-1277">Doctor</th>
                        <th className="px-6 py-4 font-semibold" data-id="element-1278">Servicio(s)</th>
                        <th className="px-6 py-4 font-semibold text-right" data-id="element-1279">
                          Ingreso Total
                        </th>
                        <th className="px-6 py-4 font-semibold text-right" data-id="element-1280">
                          Comisión
                        </th>
                        <th className="px-6 py-4 font-semibold" data-id="element-1281">
                          Registrado por
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1282">
                      {aptPagination.paginatedItems.length > 0 ? aptPagination.paginatedItems.map(apt => <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1283">
                            <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-300" data-id="element-1284">
                              {new Date(apt.date + 'T12:00:00').toLocaleDateString('es-DO')}
                            </td>
                            <td className="px-6 py-4 font-medium text-gray-900 dark:text-white" data-id="element-1285">
                              {apt.patientName}
                            </td>
                            <td className="px-6 py-4 text-gray-600 dark:text-gray-400" data-id="element-1286">
                              {apt.doctorName}
                            </td>
                            <td className="px-6 py-4 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={apt.serviceNames} data-id="element-1287">
                              {apt.serviceNames}
                            </td>
                            <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white" data-id="element-1288">
                              {formatCurrency(apt.totalRevenue)}
                            </td>
                            <td className="px-6 py-4 text-right font-bold text-emerald-600 dark:text-emerald-400" data-id="element-1289">
                              <div data-id="element-1290">{formatCurrency(apt.commission)}</div>
                              <div className="text-xs font-normal text-gray-400" data-id="element-1291">
                                ({apt.userPercent}%)
                              </div>
                            </td>
                            <td className="px-6 py-4" data-id="element-1292">
                              <Badge variant="default" data-id="element-1293">
                                {apt.registeredBy}
                              </Badge>
                            </td>
                          </tr>) : <tr data-id="element-1294">
                          <td colSpan={7} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400" data-id="element-1295">
                            No se encontraron citas completadas en este período.
                          </td>
                        </tr>}
                    </tbody>
                    {appointmentsWithCommission.length > 0 && <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-bold border-t border-gray-200 dark:border-gray-700" data-id="element-1296">
                        <tr data-id="element-1297">
                          <td colSpan={4} className="px-6 py-4 text-right text-gray-900 dark:text-white" data-id="element-1298">
                            Totales ({appointmentsWithCommission.length} citas):
                          </td>
                          <td className="px-6 py-4 text-right text-gray-900 dark:text-white" data-id="element-1299">
                            {formatCurrency(totals.revenue)}
                          </td>
                          <td className="px-6 py-4 text-right text-emerald-600 dark:text-emerald-400" data-id="element-1300">
                            {formatCurrency(totals.commission)}
                          </td>
                          <td data-id="element-1301"></td>
                        </tr>
                      </tfoot>}
                  </table>
                </div>
                <div className="px-4 pb-4" data-id="element-1302">
                  <Pagination currentPage={aptPagination.currentPage} totalPages={aptPagination.totalPages} totalItems={aptPagination.totalItems} startIndex={aptPagination.startIndex} endIndex={aptPagination.endIndex} onPageChange={aptPagination.goToPage} onNext={aptPagination.nextPage} onPrev={aptPagination.prevPage} data-id="element-1303" />
                </div>
              </Card>
    }, {
      id: 'by-user',
      label: 'Comisión por Usuario',
      content: <Card data-id="element-1304">
                <div className="overflow-x-auto" data-id="element-1305">
                  <table className="w-full text-sm text-left" data-id="element-1306">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-1307">
                      <tr data-id="element-1308">
                        <th className="px-6 py-4 font-semibold" data-id="element-1309">Usuario</th>
                        <th className="px-6 py-4 font-semibold text-center" data-id="element-1310">
                          % Comisión
                        </th>
                        <th className="px-6 py-4 font-semibold text-center" data-id="element-1311">
                          Citas
                        </th>
                        <th className="px-6 py-4 font-semibold text-right" data-id="element-1312">
                          Ingresos Generados
                        </th>
                        <th className="px-6 py-4 font-semibold text-right" data-id="element-1313">
                          Comisión Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1314">
                      {userPagination.paginatedItems.length > 0 ? userPagination.paginatedItems.map(user => <tr key={user.userId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1315">
                            <td className="px-6 py-4 font-medium text-gray-900 dark:text-white" data-id="element-1316">
                              <div className="flex items-center gap-2" data-id="element-1317">
                                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400" data-id="element-1318">
                                  <UserIcon className="w-4 h-4" data-id="element-1319" />
                                </div>
                                {user.userName}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center" data-id="element-1320">
                              <Badge variant="info" data-id="element-1321">{user.percent}%</Badge>
                            </td>
                            <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-400" data-id="element-1322">
                              <Badge variant="primary" data-id="element-1323">{user.count}</Badge>
                            </td>
                            <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white" data-id="element-1324">
                              {formatCurrency(user.revenue)}
                            </td>
                            <td className="px-6 py-4 text-right font-bold text-emerald-600 dark:text-emerald-400" data-id="element-1325">
                              {formatCurrency(user.commission)}
                            </td>
                          </tr>) : <tr data-id="element-1326">
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400" data-id="element-1327">
                            No hay datos para mostrar en este período.
                          </td>
                        </tr>}
                    </tbody>
                    {userSummary.length > 0 && <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-bold border-t border-gray-200 dark:border-gray-700" data-id="element-1328">
                        <tr data-id="element-1329">
                          <td colSpan={2} className="px-6 py-4 text-right text-gray-900 dark:text-white" data-id="element-1330">
                            Totales:
                          </td>
                          <td className="px-6 py-4 text-center text-gray-900 dark:text-white" data-id="element-1331">
                            {appointmentsWithCommission.length}
                          </td>
                          <td className="px-6 py-4 text-right text-gray-900 dark:text-white" data-id="element-1332">
                            {formatCurrency(totals.revenue)}
                          </td>
                          <td className="px-6 py-4 text-right text-emerald-600 dark:text-emerald-400" data-id="element-1333">
                            {formatCurrency(totals.commission)}
                          </td>
                        </tr>
                      </tfoot>}
                  </table>
                </div>
                <div className="px-4 pb-4" data-id="element-1334">
                  <Pagination currentPage={userPagination.currentPage} totalPages={userPagination.totalPages} totalItems={userPagination.totalItems} startIndex={userPagination.startIndex} endIndex={userPagination.endIndex} onPageChange={userPagination.goToPage} onNext={userPagination.nextPage} onPrev={userPagination.prevPage} data-id="element-1335" />
                </div>
              </Card>
    }, {
      id: 'by-doctor',
      label: 'Comisión por Doctor y Terapeuta',
      content: <Card data-id="element-1336">
                <div className="overflow-x-auto" data-id="element-1337">
                  <table className="w-full text-sm text-left" data-id="element-1338">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700" data-id="element-1339">
                      <tr data-id="element-1340">
                        <th className="px-6 py-4 font-semibold" data-id="element-1341">
                          Doctor / Terapeuta
                        </th>
                        <th className="px-6 py-4 font-semibold text-center" data-id="element-1342">
                          % Comisión
                        </th>
                        <th className="px-6 py-4 font-semibold text-center" data-id="element-1343">
                          Citas
                        </th>
                        <th className="px-6 py-4 font-semibold text-right" data-id="element-1344">
                          Ingresos Generados
                        </th>
                        <th className="px-6 py-4 font-semibold text-right" data-id="element-1345">
                          Comisión Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-1346">
                      {doctorPagination.paginatedItems.length > 0 ? doctorPagination.paginatedItems.map(doc => <tr key={doc.doctorId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-1347">
                            <td className="px-6 py-4 font-medium text-gray-900 dark:text-white" data-id="element-1348">
                              <div className="flex items-center gap-2" data-id="element-1349">
                                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400" data-id="element-1350">
                                  <StethoscopeIcon className="w-4 h-4" data-id="element-1351" />
                                </div>
                                {doc.doctorName}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center" data-id="element-1352">
                              <Badge variant="info" data-id="element-1353">{doc.percent}%</Badge>
                            </td>
                            <td className="px-6 py-4 text-center text-gray-600 dark:text-gray-400" data-id="element-1354">
                              <Badge variant="primary" data-id="element-1355">{doc.count}</Badge>
                            </td>
                            <td className="px-6 py-4 text-right font-medium text-gray-900 dark:text-white" data-id="element-1356">
                              {formatCurrency(doc.revenue)}
                            </td>
                            <td className="px-6 py-4 text-right font-bold text-emerald-600 dark:text-emerald-400" data-id="element-1357">
                              {formatCurrency(doc.commission)}
                            </td>
                          </tr>) : <tr data-id="element-1358">
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400" data-id="element-1359">
                            No hay datos de doctores para mostrar en este
                            período.
                          </td>
                        </tr>}
                    </tbody>
                    {doctorSummary.length > 0 && <tfoot className="bg-gray-50 dark:bg-gray-800/50 font-bold border-t border-gray-200 dark:border-gray-700" data-id="element-1360">
                        <tr data-id="element-1361">
                          <td colSpan={2} className="px-6 py-4 text-right text-gray-900 dark:text-white" data-id="element-1362">
                            Totales:
                          </td>
                          <td className="px-6 py-4 text-center text-gray-900 dark:text-white" data-id="element-1363">
                            {appointmentsWithCommission.length}
                          </td>
                          <td className="px-6 py-4 text-right text-gray-900 dark:text-white" data-id="element-1364">
                            {formatCurrency(doctorTotals.revenue)}
                          </td>
                          <td className="px-6 py-4 text-right text-emerald-600 dark:text-emerald-400" data-id="element-1365">
                            {formatCurrency(doctorTotals.commission)}
                          </td>
                        </tr>
                      </tfoot>}
                  </table>
                </div>
                <div className="px-4 pb-4" data-id="element-1366">
                  <Pagination currentPage={doctorPagination.currentPage} totalPages={doctorPagination.totalPages} totalItems={doctorPagination.totalItems} startIndex={doctorPagination.startIndex} endIndex={doctorPagination.endIndex} onPageChange={doctorPagination.goToPage} onNext={doctorPagination.nextPage} onPrev={doctorPagination.prevPage} data-id="element-1367" />
                </div>
              </Card>
    }]} data-id="element-1269" />
    </div>;
}